import { type Driver, getDeviceType, type Incident, isDeviceTypeCamera, type RouteStep, RouteStepType, type StopRouteStep, type VehicleRoute } from '@cobliteam/api-lib';
import type { DateRange, Sort, SortOrder, UUID, Vehicle } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { CameraIdentifier } from '@cobliteam/media';
import { Button, Divider, Table, Tooltip, Typography } from '@cobliteam/ui/components';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@cobliteam/ui/components/Table';
import Icon from '@cobliteam/ui/icons';
import moment from 'moment';
import { useCallback } from 'react';

import { IDLE_THRESHOLD_IN_MILLIS_WARN } from '../../../index';
import { type DataItem, type IncidentBarInfo, RoutesTimeline, type StopBarInfo } from '../RoutesTimeline/RoutesTimeline';
import * as styles from './styles';
import TablePagination from './TablePagination';

interface RouteRow {
  id: string;
  shouldHideActionButton?: boolean;
  vehicleRoute: VehicleRoute;
  vehicleId: UUID;
  cobliId?: string;
  vehicle?: Vehicle;
}

export interface RoutesTableProps {
  period: DateRange;
  wantedTimeSelection: DateRange;
  rows: RouteRow[];
  totalRowsLength: number;
  rowsPerPage: number;
  currentPage: number;
  sort?: Sort;
  driversById: Map<UUID, Driver>;
  onSeeOnMapClick: (deviceId: string) => void;
  onPaginationChange: (newPage: number, newRowsPerPage: number) => void;
  onRequestSort: (column: string, order: SortOrder) => void;
  onRequestAddress: (latitude: number, longitude: number) => void;
}

export const RoutesTable = ({
  period,
  wantedTimeSelection,
  rows,
  totalRowsLength,
  rowsPerPage,
  currentPage,
  sort,
  driversById,
  onSeeOnMapClick,
  onPaginationChange,
  onRequestSort,
  onRequestAddress,
}: RoutesTableProps) => {
  const MIN_SPACE_BETWEEN_INCIDENTS_IN_MINUTES = 5;
  const { t } = useTranslation('tracking');

  const columns = [
    {
      id: 'vehicle',
      label: t('vehicle'),
      sortable: true,
      Cell: ({ vehicleRoute }: { vehicleRoute: VehicleRoute }) =>
        `${vehicleRoute.vehicle.licensePlate || ''} ${vehicleRoute.vehicle.brand || ''} ${vehicleRoute.vehicle.model || ''}`.trim(),
    },
  ];

  const handleRequestSort = () => {
    const column = columns[0];
    const order = sort && sort.column === column.id && sort.order === 'desc' ? 'asc' : 'desc';

    onRequestSort(column.id.toString(), order);
  };

  const getFirstStepTime = (routeSteps: RouteStep[]) => {
    const firstStep = routeSteps[0];
    const secondStep = routeSteps.length > 1 ? routeSteps[1] : undefined;

    const isCrossDayStop = firstStep.type === RouteStepType.STOP && firstStep.startTime < period.start;
    return isCrossDayStop ? secondStep?.startTime || firstStep.endTime : firstStep.startTime;
  };

  const getLastStepTime = (lastStep: RouteStep) => {
    const isCrossDayStop = lastStep.type === RouteStepType.STOP && lastStep.endTime > period.end;
    return isCrossDayStop ? lastStep.startTime : lastStep.endTime;
  };

  const getRouteInterval = (routeSteps: RouteStep[]) => {
    if (routeSteps.length) {
      const lastStep = routeSteps[routeSteps.length - 1];

      return {
        period: [getFirstStepTime(routeSteps), getLastStepTime(lastStep)].sort() as [number, number],
      };
    }
  };

  const toChartDataStop = (routeSteps: RouteStep[], vehicleId: string) => {
    return routeSteps
      .filter((step) => filterRouteStepByTypeStop(step.type) && filterRouteStepByPeriod(step.startTime, step.endTime))
      .map((step) => step as StopRouteStep)
      .reduce((accRouteSteps, routeStep, idx) => {
        const newData: { [key: string]: StopBarInfo | undefined } = {};
        const driver = routeStep.driverId ? driversById.get(routeStep.driverId) : undefined;

        newData[`stop${idx + 1}`] = {
          period: [routeStep.startTime, routeStep.endTime],
          vehicleId,
          isIdle: routeStep.idleOnInMillis > IDLE_THRESHOLD_IN_MILLIS_WARN,
          stop: {
            ...routeStep,
            index: idx,
            driverName: driver?.name,
          },
        };
        return { ...accRouteSteps, ...newData };
      }, {});
  };

  const getIncidentPosition = (accIncidentsSteps: { [key: string]: IncidentBarInfo | undefined }, incidentStep: Incident, idx: number): [number, number] => {
    const lastIncident = accIncidentsSteps[`incident${idx - 1}`];
    if (lastIncident) {
      const incidentStepMoment = moment(incidentStep.time);
      const lastIncidentStepMoment = moment(lastIncident.position[1]);

      if (incidentStepMoment.diff(lastIncidentStepMoment, 'minutes', true) < MIN_SPACE_BETWEEN_INCIDENTS_IN_MINUTES) {
        const time = lastIncidentStepMoment.add(MIN_SPACE_BETWEEN_INCIDENTS_IN_MINUTES, 'minutes').valueOf();
        return [time, time];
      }
    }

    return [incidentStep.time, incidentStep.time];
  };

  const sortIncidentsByTimeAsc = (incidentA: Incident, incidentB: Incident) => {
    return incidentA.time - incidentB.time;
  };

  const getDriverNameByTime = (routeSteps: RouteStep[], time: number) => {
    const foundedStep = routeSteps.find((step) => step.startTime <= time && step.endTime >= time);

    if (foundedStep) {
      const driver = foundedStep.driverId ? driversById.get(foundedStep.driverId) : undefined;

      return driver?.name;
    }
  };

  const toChartDataIncident = (routeSteps: RouteStep[], incidents: Incident[], vehicleId: UUID) => {
    return incidents.sort(sortIncidentsByTimeAsc).reduce((accIncidentsSteps, incidentStep, idx) => {
      const newData: { [key: string]: IncidentBarInfo | undefined } = {};

      newData[`incident${idx + 1}`] = {
        period: [incidentStep.time, incidentStep.time],
        position: getIncidentPosition(accIncidentsSteps, incidentStep, idx + 1),
        vehicleId,
        incident: incidentStep,
        driverName: getDriverNameByTime(routeSteps, incidentStep.time),
      };
      return { ...accIncidentsSteps, ...newData };
    }, {});
  };

  const filterRouteStepByPeriod = (startTime: number, endTime: number) => {
    return startTime >= period.start && endTime <= period.end;
  };

  const filterRouteStepByTypeStop = (type: RouteStepType) => {
    return type === RouteStepType.STOP;
  };

  const toStartEndInfo = (routeStep: RouteStep, infoTime: number, vehicleId: UUID, type: 'start' | 'end') => {
    const asStop = routeStep.type === RouteStepType.STOP ? (routeStep as StopRouteStep) : undefined;
    const driver = routeStep.driverId ? driversById.get(routeStep.driverId) : undefined;

    return {
      timestamp: infoTime,
      type,
      vehicleId,
      geofences: asStop?.geofences || [],
      latitude: asStop?.latitude || undefined,
      longitude: asStop?.longitude || undefined,
      idleWithEngineOnInMillis: asStop?.idleOnInMillis || 0,
      idleTimeHighlighted: (asStop?.idleOnInMillis || 0) > IDLE_THRESHOLD_IN_MILLIS_WARN,
      driverName: driver?.name,
    };
  };

  const getRouteStartEnd = (routeSteps: RouteStep[], vehicleId: UUID, type: 'start' | 'end') => {
    if (routeSteps.length) {
      if (type === 'start') {
        const firstStep = routeSteps[0];
        const firstStepTime = getFirstStepTime(routeSteps);

        if (firstStepTime > period.start) {
          return toStartEndInfo(firstStep, firstStepTime, vehicleId, type);
        }
      } else {
        const lastStep = routeSteps[routeSteps.length - 1];
        const lastStepTime = getLastStepTime(lastStep);

        if (lastStepTime < period.end) {
          return toStartEndInfo(lastStep, lastStepTime, vehicleId, type);
        }
      }
    }
  };

  const toChartItem = ({ vehicleRoute: { vehicle, routeSteps, incidents } }: RouteRow): DataItem => ({
    vehicleId: vehicle.id,
    vehicleName: `${vehicle.licensePlate || t('no_license_plate')} ${vehicle.brand || ''} ${vehicle.model || ''}`.trim(),
    routeInterval: getRouteInterval(routeSteps),
    routeStart: getRouteStartEnd(routeSteps, vehicle.id, 'start'),
    routeEnd: getRouteStartEnd(routeSteps, vehicle.id, 'end'),
    stops: toChartDataStop(routeSteps, vehicle.id),
    incidents: toChartDataIncident(routeSteps, incidents, vehicle.id),
  });

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    onPaginationChange(0, newRowsPerPage);
  };

  const getVehicleDescription = ({ cobliId, vehicle }: RouteRow) => {
    const cameraIdentifierIcon = vehicle && isDeviceTypeCamera(getDeviceType(vehicle.deviceType)) && <CameraIdentifier hasCta={false} />;

    if (!(vehicle?.licensePlate || vehicle?.model.brand || vehicle?.model.name)) {
      return (
        <div className='flex items-center gap-2'>
          {cameraIdentifierIcon}
          <Tooltip content={cobliId || t('no_license_plate')}>
            <strong>{cobliId || t('no_license_plate')}</strong>
          </Tooltip>
        </div>
      );
    }

    const licensePlate = vehicle.licensePlate || '';
    const brandAndModel = `${vehicle.model.brand || ''} ${vehicle.model.name || ''}`.trim();

    return (
      <div className='flex items-center gap-2'>
        {cameraIdentifierIcon}
        <Typography variant='body' className='line-clamp-1' title={`${licensePlate} ${brandAndModel}`}>
          {vehicle.licensePlate && <strong>{`${licensePlate} `}</strong>}
          {brandAndModel}
        </Typography>
      </div>
    );
  };

  const renderTableRows = useCallback(
    () =>
      rows.map((row) => (
        <TableRow key={row.id}>
          <TableCell className='flex items-center justify-between gap-4 px-5 py-[18.5px]'>
            {getVehicleDescription(row)}
            <Tooltip content={t('see_on_map')}>
              <Button
                variant='ghost'
                size='sm'
                icon={<Icon name='external-link' />}
                disabled={!row.vehicleRoute.routeSteps.length}
                onClick={() => onSeeOnMapClick(row.vehicleId)}
              />
            </Tooltip>
          </TableCell>
        </TableRow>
      )),
    [rows]
  );

  return (
    <div className={styles.tableWrapper}>
      <div className='flex justify-end'>
        <TablePagination
          count={totalRowsLength}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onPageChange={(newPage) => onPaginationChange(newPage, rowsPerPage)}
          onRowsPerPageChange={handleChangeRowsPerPage}
          hiddenDivider
        />
      </div>
      <Divider className='mb-4' />

      <div className={styles.inlineTimeline}>
        <div role='keepTableInPlace' data-testid='routes-table'>
          <Table className={styles.tableStyle} aria-labelledby='Histórico de trajetos'>
            <TableHeader>
              <TableHead className='!w-64 !h-fit !p-0 border-gray-4 border-b [&>div]:h-full'>
                <div className='flex h-full items-center gap-2 pb-1'>
                  <span>{t('vehicle')}</span>
                  <Button
                    variant='ghost'
                    size='sm'
                    color='default'
                    className='size-4'
                    icon={<Icon name={sort?.order ? (sort.order === 'asc' ? 'arrow-up' : 'arrow-down') : 'arrow-up-down'} />}
                    onClick={handleRequestSort}
                  />
                </div>
              </TableHead>
            </TableHeader>
            <TableBody>{renderTableRows()}</TableBody>
          </Table>
        </div>
        <RoutesTimeline period={period} wantedTimeSelection={wantedTimeSelection} chartData={rows.map(toChartItem)} onRequestAddress={onRequestAddress} />
      </div>
      <Divider />
      <div className='flex justify-end'>
        <TablePagination
          count={totalRowsLength}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onPageChange={(newPage) => onPaginationChange(newPage, rowsPerPage)}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>
    </div>
  );
};
